import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useCaeControlsByDept } from '../../../hooks/analytics/useCaeReports.ts';
import { WidgetCard } from '../controller/shared.tsx';
import { BAR_RADIUS, CHART_AXIS_LINE, CHART_AXIS_TICK, CHART_CATEGORICAL, CHART_GRID_STROKE, CHART_LEGEND_STYLE, CHART_TOOLTIP_STYLE } from '../theme.ts';

/**
 * Report #17 — Controls by Department. Grouped bar chart, x-axis =
 * department, bars = total/active/key controls; a table toggle drills into
 * the per-department, per-control-type breakdown the backend returns.
 */
export const ControlsByDeptChart: React.FC<{ deptFilter?: string | null }> = ({ deptFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCaeControlsByDept();
  const rows = useMemo(() => {
    const base = data ?? [];
    return deptFilter ? base.filter((r) => r.dept === deptFilter) : base;
  }, [data, deptFilter]);
  const [showTable, setShowTable] = useState(false);

  const chartData = useMemo(() => {
    const byDept = new Map<string, { dept: string; total: number; active: number; key_ctrl: number }>();
    rows.forEach((row) => {
      const entry = byDept.get(row.dept) ?? { dept: row.dept, total: 0, active: 0, key_ctrl: 0 };
      entry.total += row.total;
      entry.active += row.active;
      entry.key_ctrl += row.key_ctrl;
      byDept.set(row.dept, entry);
    });
    return Array.from(byDept.values());
  }, [rows]);

  return (
    <WidgetCard
      title="Controls by Department"
      description="Total / active / key controls per department"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load controls by department.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="No controls found."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
      action={
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="text-xs font-medium text-blue-600 hover:text-blue-800"
        >
          {showTable ? 'Show chart' : 'Show table'}
        </button>
      }
    >
      {showTable ? (
        <div className="analytics-scroll max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="pb-2">Department</th>
                <th className="pb-2">Type</th>
                <th className="pb-2 text-right">Total</th>
                <th className="pb-2 text-right">Active</th>
                <th className="pb-2 text-right">Key</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row, idx) => (
                <tr key={`${row.dept}-${row.control_type}-${idx}`} className="hover:bg-slate-50">
                  <td className="py-1.5 pr-2 text-slate-900">{row.dept}</td>
                  <td className="py-1.5 pr-2 text-slate-500 capitalize">{row.control_type}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-slate-600">{row.total}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-slate-600">{row.active}</td>
                  <td className="py-1.5 text-right tabular-nums text-slate-600">{row.key_ctrl}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
              <XAxis dataKey="dept" tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} />
              <YAxis tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
              <Legend wrapperStyle={CHART_LEGEND_STYLE} />
              <Bar dataKey="total" name="Total" fill={CHART_CATEGORICAL[0]} radius={BAR_RADIUS} maxBarSize={28} />
              <Bar dataKey="active" name="Active" fill={CHART_CATEGORICAL[1]} radius={BAR_RADIUS} maxBarSize={28} />
              <Bar dataKey="key_ctrl" name="Key" fill={CHART_CATEGORICAL[2]} radius={BAR_RADIUS} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </WidgetCard>
  );
};

export default ControlsByDeptChart;
